import { observer } from 'mobx-react-lite';
import React from 'react';
import { LocationStore } from '../../location-store';
import { Flex, Table, Tbody, Thead, Tr, Box, Spinner, Text } from '@cad-arc';
import { LocationTableHeader } from './location-table-header';
import { LocationTableAddressRow } from './location-table-address-row';
import { useLocationList } from '../../hooks/use-location-list';
import { CommonSize, getOneSizeSmaller } from '../../../common/helpers/utils';

export interface LocationListProps {
    store: LocationStore;
    query: string;
    isLoadingResults: boolean;
    onClickRow?: (id: number) => void;
    moveFocusNext?: () => void;
    inputRef?: React.MutableRefObject<HTMLInputElement | null>;
    size?: CommonSize;
    listBoxId?: string;
    optionIdPrefix: string;
}

export const LocationTable: React.FC<LocationListProps> = observer(
    ({
        store,
        isLoadingResults,
        query,
        onClickRow,
        moveFocusNext,
        inputRef,
        size,
        listBoxId,
        optionIdPrefix,
    }) => {
        const locationList = useLocationList({ store, onClickRow, moveFocusNext, inputRef });
        const {
            mark43Items,
            esriItems,
            esriCustomItems,
            latLngItems,
            forcedResults,
        } = locationList.data;
        const { onRowSelected, updateSort } = locationList.operations;
        const { sortMode } = locationList.state;
        const smallerSize = getOneSizeSmaller(size);
        const headerSize = getOneSizeSmaller(smallerSize);

        return (
            <Box h="300px" overflowY="scroll" data-testid="LOCATION_SELECT_TABLE">
                {isLoadingResults ? (
                    <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
                        <Spinner />
                    </Flex>
                ) : (
                    <Table size="md" width="100%">
                        <Thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'var(--arc-colors-surface-foreground)',
                            }}
                        >
                            <Tr style={{ cursor: 'pointer' }}>
                                <LocationTableHeader
                                    currentSortMode={sortMode}
                                    sortOption="ADDRESS"
                                    updateSort={updateSort}
                                    key="address"
                                >
                                    <Text fontSize={headerSize} color="secondary" fontWeight="bold">
                                        Address
                                    </Text>
                                </LocationTableHeader>
                                <LocationTableHeader
                                    currentSortMode={sortMode}
                                    sortOption="D-AREA"
                                    updateSort={updateSort}
                                    key="d-area"
                                >
                                    <Text fontSize={headerSize} color="secondary" fontWeight="bold">
                                        D-Area
                                    </Text>
                                </LocationTableHeader>

                                <LocationTableHeader
                                    currentSortMode={sortMode}
                                    sortOption="CROSS STREET"
                                    updateSort={updateSort}
                                    key="cross-streets"
                                >
                                    <Text fontSize={headerSize} color="secondary" fontWeight="bold">
                                        Cross Streets
                                    </Text>
                                </LocationTableHeader>
                            </Tr>
                        </Thead>
                        <Tbody
                            id={listBoxId}
                            role="listbox"
                            data-testid="location-select-results-listbox"
                        >
                            {esriCustomItems.length > 0 ? (
                                <>
                                    {esriCustomItems.map((row, index) => (
                                        <LocationTableAddressRow
                                            onClick={() => onRowSelected(row)}
                                            key={row.id}
                                            row={row}
                                            query={query}
                                            store={store}
                                            index={index}
                                            size={size}
                                            optionIdPrefix={optionIdPrefix}
                                        />
                                    ))}
                                </>
                            ) : undefined}
                            {mark43Items.map((row, index) => {
                                return (
                                    <LocationTableAddressRow
                                        onClick={() => onRowSelected(row)}
                                        key={row.id}
                                        row={row}
                                        query={query}
                                        store={store}
                                        index={index + esriCustomItems.length}
                                        size={size}
                                        optionIdPrefix={optionIdPrefix}
                                    />
                                );
                            })}
                            {esriItems.length > 0 ? (
                                <>
                                    <Box pl={3} pt={3} color="brand.default" bg="surface.primary">
                                        <Text fontSize={smallerSize}>Results from ESRI</Text>
                                    </Box>
                                    {esriItems.map((row, index) => {
                                        return (
                                            <LocationTableAddressRow
                                                onClick={() => onRowSelected(row)}
                                                key={row.id}
                                                row={row}
                                                query={query}
                                                store={store}
                                                index={
                                                    index +
                                                    esriCustomItems.length +
                                                    mark43Items.length
                                                }
                                                size={size}
                                                optionIdPrefix={optionIdPrefix}
                                            />
                                        );
                                    })}
                                </>
                            ) : undefined}
                            {latLngItems.length > 0 ? (
                                <>
                                    {latLngItems.map((row, index) => {
                                        return (
                                            <LocationTableAddressRow
                                                onClick={() => onRowSelected(row)}
                                                key={row.id}
                                                row={row}
                                                query={query}
                                                store={store}
                                                index={
                                                    index +
                                                    esriCustomItems.length +
                                                    esriItems.length +
                                                    mark43Items.length
                                                }
                                                size={size}
                                                optionIdPrefix={optionIdPrefix}
                                            />
                                        );
                                    })}
                                </>
                            ) : undefined}
                            {forcedResults.map((row, index) => {
                                return (
                                    <LocationTableAddressRow
                                        onClick={() => onRowSelected(row)}
                                        key={row.id}
                                        row={row}
                                        query={query}
                                        store={store}
                                        index={
                                            index +
                                            esriCustomItems.length +
                                            mark43Items.length +
                                            esriItems.length +
                                            latLngItems.length
                                        }
                                        size={size}
                                        optionIdPrefix={optionIdPrefix}
                                    />
                                );
                            })}
                        </Tbody>
                    </Table>
                )}
            </Box>
        );
    }
);
